import { DOMAINS, type Domain } from "../../domain/contracts";
import {
  cacheVisitedIslandResources,
  type CacheVisitedIslandResult
} from "../../platform/pwa/cacheVisitedIsland";
import type { CacheFailureReason } from "../../platform/pwa/types";
import {
  createVisitedIslandResourcePlan,
  type VisitedIslandResourceCatalog,
  type VisitedIslandResourcePlan
} from "./visitedIslandResources";

export interface OfflinePlaySessionIdentity {
  readonly domain: Domain;
  readonly profileId: string;
  /** Must increase for every play accepted by one coordinator instance. */
  readonly sessionToken: number;
}

export type OfflineLifecycleBoundary =
  | ({ readonly kind: "map"; readonly visitedDomain?: Domain } &
      OfflinePlaySessionIdentity)
  | ({ readonly kind: "play" } & OfflinePlaySessionIdentity)
  | ({ readonly kind: "result" } & OfflinePlaySessionIdentity);

export type VisitedIslandOfflinePhase =
  | "idle"
  | "preparing"
  | "ready"
  | "failed";

export interface VisitedIslandOfflineProgress {
  readonly domain: Domain;
  readonly phase: VisitedIslandOfflinePhase;
  readonly attempt: number;
  readonly resourceCount: number;
  readonly cachedResourceCount: number;
  readonly skippedResourceCount: number;
  readonly failedResources: readonly string[];
  readonly failureReason: CacheFailureReason | null;
}

export interface VisitedIslandOfflineSnapshot {
  readonly islands: ReadonlyMap<Domain, VisitedIslandOfflineProgress>;
}

export interface OfflineUpdateSafetyController {
  setUpdateSafe(isUpdateSafe: boolean): void;
  reportCacheFailure?(reason: CacheFailureReason): void;
  reportCacheReady(): void;
}

export type CacheVisitedIsland = (options: {
  readonly domain: Domain;
  readonly resources: readonly string[];
}) => Promise<CacheVisitedIslandResult>;

export type VisitedIslandResourcePlanner = (
  domain: Domain,
  catalog: VisitedIslandResourceCatalog
) => VisitedIslandResourcePlan;

export interface VisitedIslandOfflineCoordinatorDependencies {
  readonly resourceCatalog: VisitedIslandResourceCatalog;
  readonly updateSafety: OfflineUpdateSafetyController;
  readonly cacheResources?: CacheVisitedIsland;
  readonly planResources?: VisitedIslandResourcePlanner;
}

function createIdleProgress(domain: Domain): VisitedIslandOfflineProgress {
  return {
    domain,
    phase: "idle",
    attempt: 0,
    resourceCount: 0,
    cachedResourceCount: 0,
    skippedResourceCount: 0,
    failedResources: [],
    failureReason: null
  };
}

export class VisitedIslandOfflineCoordinator {
  private readonly resourceCatalog: VisitedIslandResourceCatalog;
  private readonly updateSafety: OfflineUpdateSafetyController;
  private readonly cacheResources: CacheVisitedIsland;
  private readonly planResources: VisitedIslandResourcePlanner;
  private readonly listeners = new Set<() => void>();
  private readonly plans = new Map<Domain, VisitedIslandResourcePlan>();
  private readonly inFlight = new Map<Domain, Promise<void>>();
  private readonly cachedResources = new Map<Domain, Set<string>>();
  private readonly skippedResources = new Map<Domain, Set<string>>();
  private readonly progress = new Map<Domain, VisitedIslandOfflineProgress>(
    DOMAINS.map(
      (domain): readonly [Domain, VisitedIslandOfflineProgress] => [
        domain,
        createIdleProgress(domain)
      ]
    )
  );
  private snapshot: VisitedIslandOfflineSnapshot = {
    islands: new Map(this.progress)
  };
  private latestPreparationGeneration = 0;
  private latestSessionToken = -1;
  private activePlay: OfflinePlaySessionIdentity | null = null;

  constructor({
    resourceCatalog,
    updateSafety,
    cacheResources = cacheVisitedIslandResources,
    planResources = createVisitedIslandResourcePlan
  }: VisitedIslandOfflineCoordinatorDependencies) {
    this.resourceCatalog = resourceCatalog;
    this.updateSafety = updateSafety;
    this.cacheResources = cacheResources;
    this.planResources = planResources;
  }

  readonly getSnapshot = (): VisitedIslandOfflineSnapshot => this.snapshot;

  readonly subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  };

  getIslandProgress(domain: Domain): VisitedIslandOfflineProgress {
    const progress = this.progress.get(domain);
    if (progress === undefined) {
      throw new Error("Missing offline progress for " + domain);
    }
    return progress;
  }

  notifyBoundary(boundary: OfflineLifecycleBoundary): void {
    switch (boundary.kind) {
      case "map":
        if (this.matchesActivePlay(boundary)) {
          this.activePlay = null;
          this.updateSafety.setUpdateSafe(true);
        }
        if (boundary.visitedDomain !== undefined) {
          this.startPreparation(boundary.visitedDomain);
        }
        return;
      case "play":
        if (!this.acceptPlayBoundary(boundary)) {
          return;
        }
        this.updateSafety.setUpdateSafe(false);
        this.startPreparation(boundary.domain);
        return;
      case "result":
        if (!this.matchesActivePlay(boundary)) {
          return;
        }
        this.activePlay = null;
        this.updateSafety.setUpdateSafe(true);
        return;
    }
  }

  async waitForPreparation(
    domain: Domain
  ): Promise<VisitedIslandOfflineProgress> {
    await this.inFlight.get(domain);
    return this.getIslandProgress(domain);
  }

  private startPreparation(domain: Domain): void {
    const current = this.getIslandProgress(domain);
    if (current.phase === "ready" || this.inFlight.has(domain)) {
      return;
    }

    const preparationGeneration = ++this.latestPreparationGeneration;
    let plan: VisitedIslandResourcePlan;
    try {
      plan = this.getPlan(domain);
    } catch {
      this.setProgress({
        ...current,
        phase: "failed",
        attempt: current.attempt + 1,
        failedResources: [],
        failureReason: "unknown"
      });
      this.publishPreparationOutcome(preparationGeneration, "unknown");
      return;
    }

    const resources =
      current.phase === "failed" && current.failedResources.length > 0
        ? current.failedResources
        : plan.resources;

    this.setProgress({
      ...current,
      phase: "preparing",
      attempt: current.attempt + 1,
      resourceCount: plan.resources.length,
      failedResources: [],
      failureReason: null
    });

    const preparation = this.runPreparation(
      domain,
      preparationGeneration,
      plan,
      resources
    );
    this.inFlight.set(domain, preparation);
    void preparation.then(() => {
      if (this.inFlight.get(domain) === preparation) {
        this.inFlight.delete(domain);
      }
    });
  }

  private async runPreparation(
    domain: Domain,
    preparationGeneration: number,
    plan: VisitedIslandResourcePlan,
    resources: readonly string[]
  ): Promise<void> {
    let result: CacheVisitedIslandResult;
    try {
      result = await this.cacheResources({ domain, resources });
    } catch {
      this.finishFailedPreparation(
        domain,
        preparationGeneration,
        plan,
        resources,
        "unknown"
      );
      return;
    }

    const cached = this.readResourceSet(this.cachedResources, domain);
    const skipped = this.readResourceSet(this.skippedResources, domain);
    result.cachedResources.forEach((resource) => cached.add(resource));
    result.skippedResources.forEach((resource) => skipped.add(resource));

    if (result.isComplete) {
      const current = this.getIslandProgress(domain);
      this.setProgress({
        ...current,
        phase: "ready",
        resourceCount: plan.resources.length,
        cachedResourceCount: cached.size,
        skippedResourceCount: skipped.size,
        failedResources: [],
        failureReason: null
      });
      this.publishPreparationOutcome(preparationGeneration, null);
      return;
    }

    this.finishFailedPreparation(
      domain,
      preparationGeneration,
      plan,
      result.failedResources.length > 0 ? result.failedResources : resources,
      result.failureReason ?? "unknown"
    );
  }

  private finishFailedPreparation(
    domain: Domain,
    preparationGeneration: number,
    plan: VisitedIslandResourcePlan,
    failedResources: readonly string[],
    failureReason: CacheFailureReason
  ): void {
    const current = this.getIslandProgress(domain);
    this.setProgress({
      ...current,
      phase: "failed",
      resourceCount: plan.resources.length,
      cachedResourceCount:
        this.cachedResources.get(domain)?.size ?? current.cachedResourceCount,
      skippedResourceCount:
        this.skippedResources.get(domain)?.size ?? current.skippedResourceCount,
      failedResources: [...new Set(failedResources)],
      failureReason
    });
    this.publishPreparationOutcome(preparationGeneration, failureReason);
  }

  private publishPreparationOutcome(
    preparationGeneration: number,
    failureReason: CacheFailureReason | null
  ): void {
    if (preparationGeneration !== this.latestPreparationGeneration) {
      return;
    }

    if (failureReason === null) {
      this.updateSafety.reportCacheReady();
      return;
    }

    this.updateSafety.reportCacheFailure?.(failureReason);
  }

  private getPlan(domain: Domain): VisitedIslandResourcePlan {
    const existing = this.plans.get(domain);
    if (existing !== undefined) {
      return existing;
    }
    const plan = this.planResources(domain, this.resourceCatalog);
    this.plans.set(domain, plan);
    return plan;
  }

  private acceptPlayBoundary(boundary: OfflinePlaySessionIdentity): boolean {
    if (
      !Number.isSafeInteger(boundary.sessionToken) ||
      boundary.sessionToken < 0 ||
      boundary.sessionToken <= this.latestSessionToken
    ) {
      return false;
    }

    this.latestSessionToken = boundary.sessionToken;
    this.activePlay = {
      domain: boundary.domain,
      profileId: boundary.profileId,
      sessionToken: boundary.sessionToken
    };
    return true;
  }

  private matchesActivePlay(boundary: OfflinePlaySessionIdentity): boolean {
    return (
      this.activePlay !== null &&
      boundary.sessionToken === this.activePlay.sessionToken &&
      boundary.domain === this.activePlay.domain &&
      boundary.profileId === this.activePlay.profileId
    );
  }

  private readResourceSet(
    resourceSets: Map<Domain, Set<string>>,
    domain: Domain
  ): Set<string> {
    const existing = resourceSets.get(domain);
    if (existing !== undefined) {
      return existing;
    }
    const resources = new Set<string>();
    resourceSets.set(domain, resources);
    return resources;
  }

  private setProgress(progress: VisitedIslandOfflineProgress): void {
    this.progress.set(progress.domain, progress);
    this.snapshot = { islands: new Map(this.progress) };
    for (const listener of this.listeners) {
      listener();
    }
  }
}
